// ==================== SMS MESSAGES PAGE ====================
// Integrated SMS Messaging Interface with Backend

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Search,
  Plus,
  Send,
  RefreshCw,
  Phone,
  User,
  Clock,
  CheckCircle,
  CheckCheck,
  AlertCircle,
  X,
  Settings,
  MoreVertical,
  Globe,
  Loader2,
  FileText,
  ChevronDown,
  Sparkles,
} from 'lucide-react';
import { Button, Input, Badge } from '../../../components/ui';
import { Link, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes.constants';
import {
  getSmsAccount,
  getSmsConversations,
  getSmsMessages,
  sendSms,
} from '../../../services/sms.service';
import type {
  SmsMessage as ApiSmsMessage,
  SmsConversation as ApiSmsConversation,
} from '../../../services/sms.service';
import { contactsService } from '../../../services/contacts.service';
import { smsTemplatesService, type SmsTemplate } from '../../../services/sms-templates.service';
import type { Contact } from '../../../types';

// ==================== TYPES ====================

interface SmsContact {
  id: string;
  name: string | null;
  phoneNumber: string;
}

interface SmsMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  content: string;
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'READ';
  createdAt: string;
  deliveredAt?: string;
}

interface SmsConversation {
  contactId: string;
  contactName: string | null;
  contactPhone: string;
  lastMessage: {
    content: string;
    direction: 'INBOUND' | 'OUTBOUND';
    createdAt: string;
    status: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'READ';
  } | null;
  unreadCount: number;
  lastMessageAt: string;
}

// ==================== HELPER: Transform API types to local types ====================

const transformApiConversation = (conv: ApiSmsConversation): SmsConversation => ({
  contactId: conv.contactId,
  contactName: conv.contactName ?? null,
  contactPhone: conv.contactPhone,
  lastMessage: conv.lastMessage ? {
    content: conv.lastMessage.content,
    direction: conv.lastMessage.direction as 'INBOUND' | 'OUTBOUND',
    createdAt: String(conv.lastMessage.createdAt),
    status: conv.lastMessage.status as SmsMessage['status'],
  } : null,
  unreadCount: conv.unreadCount,
  lastMessageAt: String(conv.lastMessageAt),
});

const transformApiMessage = (msg: ApiSmsMessage): SmsMessage => ({
  id: msg.id,
  direction: msg.direction as 'INBOUND' | 'OUTBOUND',
  content: msg.content,
  status: msg.status as SmsMessage['status'],
  createdAt: String(msg.createdAt),
  deliveredAt: msg.deliveredAt ? String(msg.deliveredAt) : undefined,
});

// ==================== HELPER FUNCTIONS ====================

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString();
};

const formatMessageTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// ==================== STATUS ICON COMPONENT ====================

const StatusIcon = ({ status }: { status: SmsMessage['status'] }) => {
  switch (status) {
    case 'PENDING':
      return <Clock className="w-3 h-3 text-gray-400" />;
    case 'SENT':
      return <CheckCircle className="w-3 h-3 text-gray-400" />;
    case 'DELIVERED':
      return <CheckCheck className="w-3 h-3 text-green-400" />;
    case 'FAILED':
      return <AlertCircle className="w-3 h-3 text-red-400" />;
    default:
      return null;
  }
};

// ==================== CONVERSATION ITEM COMPONENT ====================

const ConversationItem = ({
  conversation,
  isActive,
  onClick,
}: {
  conversation: SmsConversation;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`
      w-full p-3 flex items-start gap-3 transition-colors text-left
      ${isActive 
        ? 'bg-green-500/10 border-l-2 border-green-500' 
        : 'hover:bg-[#1A2332] border-l-2 border-transparent'
      }
    `}
  >
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shrink-0">
      <span className="text-white font-medium text-sm">
        {(conversation.contactName || conversation.contactPhone).charAt(0).toUpperCase()}
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className={`font-medium text-sm truncate ${isActive ? 'text-green-400' : 'text-white'}`}>
          {conversation.contactName || conversation.contactPhone}
        </span>
        <span className="text-[10px] text-gray-500 shrink-0">
          {formatTime(conversation.lastMessageAt)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <p className="text-xs text-gray-500 truncate flex-1">
          {conversation.lastMessage ? (
            <>
              {conversation.lastMessage.direction === 'OUTBOUND' && (
                <span className="text-gray-600">You: </span>
              )}
              {conversation.lastMessage.content}
            </>
          ) : (
            <span className="text-gray-600 italic">No messages yet</span>
          )}
        </p>
        {conversation.unreadCount > 0 && (
          <span className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] font-medium flex items-center justify-center shrink-0">
            {conversation.unreadCount}
          </span>
        )}
      </div>
    </div>
  </button>
);

// ==================== DATE SEPARATOR (WhatsApp-style) ====================
/**
 * Formats a date for display as a separator between messages.
 * Shows "Today", "Yesterday", or the full date for older messages.
 */
const formatMessageDate = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const messageDate = new Date(date);
  
  // Reset time parts for comparison
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayStart = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  const messageDateStart = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
  
  if (messageDateStart.getTime() === todayStart.getTime()) {
    return 'Today';
  } else if (messageDateStart.getTime() === yesterdayStart.getTime()) {
    return 'Yesterday';
  } else {
    // Check if it's within the current year
    if (messageDate.getFullYear() === today.getFullYear()) {
      return messageDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric' 
      });
    } else {
      return messageDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};

/**
 * Gets the date key for grouping messages (YYYY-MM-DD format)
 */
const getDateKey = (date: Date | string): string => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Date separator shown between messages from different days
 */
const DateSeparator = ({ date }: { date: string }) => {
  const formattedDate = formatMessageDate(new Date(date));
  
  return (
    <div className="flex items-center justify-center my-4">
      <div className="px-3 py-1 bg-[#1A2332] rounded-lg shadow-sm">
        <span className="text-xs text-gray-400 font-medium">{formattedDate}</span>
      </div>
    </div>
  );
};

// ==================== MESSAGE BUBBLE COMPONENT ====================

const MessageBubble = ({ message, isNew = false }: { message: SmsMessage; isNew?: boolean }) => {
  const isOutbound = message.direction === 'OUTBOUND';

  // Only animate new messages to prevent flickering on initial load
  const animationProps = isNew 
    ? { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } }
    : { initial: false, animate: { opacity: 1, y: 0 } };

  return (
    <motion.div
      {...animationProps}
      transition={{ duration: 0.15 }}
      className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-2`}
    >
      <div
        className={`
          max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm
          ${isOutbound 
            ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-br-md' 
            : 'bg-[#1A2332] text-gray-200 rounded-bl-md'
          }
        `}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        <div className={`flex items-center gap-1.5 mt-1 ${isOutbound ? 'justify-end' : 'justify-start'}`}>
          <span className={`text-[10px] ${isOutbound ? 'text-green-100/70' : 'text-gray-500'}`}>
            {formatMessageTime(message.createdAt)}
          </span>
          {isOutbound && <StatusIcon status={message.status} />}
        </div>
      </div>
    </motion.div>
  );
};

// ==================== NEW SMS MODAL (LAZY LOADED) ====================

const CONTACTS_PAGE_SIZE = 20;

const NewSmsModal = ({
  isOpen,
  onClose,
  onSelect,
  existingConversationIds = [],
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (contact: SmsContact) => void;
  existingConversationIds?: string[];
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      setContacts([]);
      setHasMore(true);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  // Fetch contacts function
  const fetchContacts = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await contactsService.getAll({
        limit: CONTACTS_PAGE_SIZE,
        page: pageNum,
        search: debouncedSearch || undefined,
      });

      const newContacts = response.items || [];
      const total = response.meta?.total || 0;

      if (reset) {
        setContacts(newContacts);
      } else {
        setContacts((prev) => [...prev, ...newContacts]);
      }

      setTotalCount(total);
      setHasMore(newContacts.length === CONTACTS_PAGE_SIZE);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch]);

  // Fetch contacts when modal opens or search changes
  useEffect(() => {
    if (isOpen) {
      fetchContacts(1, true);
    }
  }, [isOpen, debouncedSearch, fetchContacts]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setContacts([]);
      setSearch('');
      setDebouncedSearch('');
      setPage(1);
      setHasMore(true);
      setTotalCount(0);
      setPhoneNumber('');
    }
  }, [isOpen]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when user scrolls to 80% of the list
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      fetchContacts(page + 1, false);
    }
  }, [loadingMore, hasMore, loading, page, fetchContacts]);

  const handleSendToNumber = () => {
    if (phoneNumber.trim()) {
      onSelect({
        id: `new-${Date.now()}`,
        name: null,
        phoneNumber: phoneNumber.trim(),
      });
      onClose();
    }
  };

  // Separate contacts with existing conversations and new contacts
  const existingContacts = contacts.filter((c) => existingConversationIds.includes(c.id));
  const newContacts = contacts.filter((c) => !existingConversationIds.includes(c.id));

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#121723] border border-gray-800 rounded-xl shadow-2xl w-full max-w-sm max-h-[80vh] overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3 border-b border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-green-500" />
              <h3 className="text-sm font-heading font-semibold text-white">New SMS</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Direct phone number input */}
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="+923001234567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              leftIcon={<Phone className="w-3.5 h-3.5" />}
              className="flex-1"
            />
            <Button 
              onClick={handleSendToNumber} 
              disabled={!phoneNumber.trim()}
              size="sm"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>

          <div className="relative mb-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-[10px]">
              <span className="bg-[#121723] px-2 text-gray-500">or select a contact</span>
            </div>
          </div>

          {/* Search */}
          <Input
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-3.5 h-3.5" />}
            autoFocus
          />
          {totalCount > 0 && (
            <p className="text-[10px] text-gray-500 mt-1.5">
              {contacts.length} of {totalCount.toLocaleString()} contacts
            </p>
          )}
        </div>

        {/* Contact List */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="max-h-[320px] overflow-y-auto"
        >
          {loading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="w-5 h-5 text-green-500 animate-spin" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">No contacts found</p>
              <p className="text-[10px] text-gray-500 mt-1">
                {debouncedSearch ? 'Try a different search term' : 'Add contacts or enter a phone number above'}
              </p>
            </div>
          ) : (
            <>
              {/* New contacts section */}
              {newContacts.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 bg-[#0D1117] text-[10px] font-medium text-gray-500 uppercase tracking-wider sticky top-0">
                    New Conversation
                  </div>
                  {newContacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => {
                        onSelect({
                          id: contact.id,
                          name: contact.name || null,
                          phoneNumber: contact.phoneNumber,
                        });
                        onClose();
                      }}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[#1A2332] transition-colors border-b border-gray-800/50"
                    >
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                        {(contact.name || contact.phoneNumber).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">{contact.name || contact.phoneNumber}</h4>
                        <p className="text-[11px] text-gray-400">{contact.phoneNumber}</p>
                      </div>
                      <span className="text-[10px] font-medium text-green-500 shrink-0">NEW</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Existing conversations section */}
              {existingContacts.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 bg-[#0D1117] text-[10px] font-medium text-gray-500 uppercase tracking-wider sticky top-0">
                    Existing Conversations
                  </div>
                  {existingContacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => {
                        onSelect({
                          id: contact.id,
                          name: contact.name || null,
                          phoneNumber: contact.phoneNumber,
                        });
                        onClose();
                      }}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[#1A2332] transition-colors border-b border-gray-800/50"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                        {(contact.name || contact.phoneNumber).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">{contact.name || contact.phoneNumber}</h4>
                        <p className="text-[11px] text-gray-400">{contact.phoneNumber}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex items-center justify-center p-3">
                  <Loader2 className="w-4 h-4 text-green-500 animate-spin" />
                  <span className="text-[10px] text-gray-400 ml-2">Loading more...</span>
                </div>
              )}

              {/* End of list indicator */}
              {!hasMore && contacts.length > 0 && (
                <div className="p-2 text-center">
                  <p className="text-[10px] text-gray-500">All contacts loaded</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 py-2 border-t border-gray-800 bg-[#0D1117]">
          <p className="text-[10px] text-gray-500 text-center">
            💬 Select a contact or enter a phone number to send SMS
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== SMS TEMPLATE SELECTOR ====================

interface SmsTemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: SmsTemplate) => void;
  contactName?: string | null;
}

const SmsTemplateSelector = ({ isOpen, onClose, onSelect, contactName }: SmsTemplateSelectorProps) => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasFetchedRef.current = false;
      return;
    }
    
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const result = await smsTemplatesService.getAll({ isActive: true, limit: 50 });
        setTemplates(result.data || []);
      } catch (err) {
        console.error('Failed to load SMS templates:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [isOpen]);

  const filteredTemplates = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.content.toLowerCase().includes(search.toLowerCase())
  );

  // Generate preview by replacing variables with sample data
  const getPreview = (content: string) => {
    let preview = content;
    const sampleData: Record<string, string> = {
      name: contactName || 'John',
      first_name: contactName?.split(' ')[0] || 'John',
      company: 'Acme Inc',
      phone: '+1234567890',
      email: 'john@example.com',
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    
    for (const [key, value] of Object.entries(sampleData)) {
      preview = preview.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'gi'), value);
    }
    return preview;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#121723] border border-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-500" />
              <h3 className="text-base font-semibold text-white">Select SMS Template</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>

        {/* Templates List */}
        <div ref={scrollContainerRef} className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 text-green-500 animate-spin" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400">
                {search ? 'No templates match your search' : 'No active templates found'}
              </p>
              <Link to={ROUTES.CUSTOMER.SMS_TEMPLATES} className="text-xs text-green-500 hover:underline mt-2 inline-block">
                Create a template
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-800/50">
              {filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    onSelect(template);
                    onClose();
                  }}
                  className="w-full p-4 text-left hover:bg-[#1A2332] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-white truncate">{template.name}</h4>
                      {template.description && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{template.description}</p>
                      )}
                    </div>
                    <Badge variant="default" className="text-[10px] shrink-0">
                      {template.segmentCount} SMS
                    </Badge>
                  </div>
                  <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800/50">
                    <p className="text-xs text-gray-400 line-clamp-3 whitespace-pre-wrap">
                      {getPreview(template.content)}
                    </p>
                  </div>
                  {template.variables && (template.variables as unknown[]).length > 0 && (
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                      <Sparkles className="w-3 h-3 text-gray-500" />
                      {((template.variables as unknown[]) as { variable: string }[]).slice(0, 3).map((v) => (
                        <span key={v.variable} className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 font-mono">
                          {`{{${v.variable}}}`}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-800 bg-[#0D1117]">
          <p className="text-[11px] text-gray-500 text-center">
            Select a template to preview and send. Variables will be replaced with contact data.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================

const SmsMessagesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialContactId = searchParams.get('contactId');
  
  const [conversations, setConversations] = useState<SmsConversation[]>([]);
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewSmsModal, setShowNewSmsModal] = useState(false);
  const [smsConfigured, setSmsConfigured] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Template state
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSmsConversations(undefined, 1, 50);
      const transformed = response.items.map(transformApiConversation);
      setConversations(transformed);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if SMS is configured
  useEffect(() => {
    const checkSmsConfiguration = async () => {
      try {
        const account = await getSmsAccount();
        setSmsConfigured(account !== null && account.status === 'ACTIVE');
        if (account && account.status === 'ACTIVE') {
          loadConversations();
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking SMS configuration:', err);
        setSmsConfigured(false);
        setLoading(false);
      }
    };

    checkSmsConfiguration();
  }, [loadConversations]);

  // Track if we've processed the initial contact ID from URL
  const processedInitialContactRef = useRef(false);
  
  // Auto-select contact when page loads (from URL param or first conversation)
  useEffect(() => {
    if (!smsConfigured || loading) return;
    
    // If contactId is provided in URL and we haven't processed it yet
    if (initialContactId && !processedInitialContactRef.current) {
      processedInitialContactRef.current = true;
      
      // Check if this contact already has a conversation
      const existingConversation = conversations.find((c) => c.contactId === initialContactId);
      
      if (existingConversation) {
        // Contact has existing conversation, select it
        setSelectedContact(initialContactId);
        setSelectedPhone(existingConversation.contactPhone);
      } else {
        // Contact has no SMS conversation, fetch contact info and set up new chat
        setSelectedContact(initialContactId);
        
        const setupNewChat = async () => {
          try {
            const contact = await contactsService.getById(initialContactId);
            setSelectedPhone(contact.phoneNumber);
            setMessages([]);
          } catch (err) {
            console.error('Failed to fetch contact for new SMS chat:', err);
            // If contact not found, select first conversation
            if (conversations.length > 0) {
              setSelectedContact(conversations[0].contactId);
              setSelectedPhone(conversations[0].contactPhone);
            }
          }
        };
        setupNewChat();
      }
      // Clear the URL param after using it
      setSearchParams({}, { replace: true });
      return;
    }
    
    // Otherwise, if no contact selected yet, select first conversation
    if (!selectedContact && conversations.length > 0 && !initialContactId) {
      setSelectedContact(conversations[0].contactId);
      setSelectedPhone(conversations[0].contactPhone);
    }
  }, [conversations, selectedContact, smsConfigured, loading, initialContactId, setSearchParams]);

  // Load messages when contact selected
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedContact) {
        setMessages([]);
        return;
      }

      // Skip loading for new contacts (those starting with 'new-')
      if (selectedContact.startsWith('new-')) {
        setMessages([]);
        return;
      }

      // Reset tracking refs when switching contacts
      isInitialLoadRef.current = true;
      loadedMessageIdsRef.current.clear();

      try {
        setLoadingMessages(true);
        const response = await getSmsMessages(selectedContact, 1, 100);
        const transformed = response.items.map(transformApiMessage);
        setMessages(transformed);
        setLoadingMessages(false);
      } catch (err) {
        console.error('Error loading messages:', err);
        setError('Failed to load messages');
        setLoadingMessages(false);
      }
    };

    loadMessages();
  }, [selectedContact]);

  const scrollToBottom = useCallback((instant = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const doScroll = () => {
      const target = container.scrollHeight - container.clientHeight;
      if (target <= 0) return;
      if (instant) {
        container.scrollTop = target;
      } else {
        try {
          container.scrollTo({ top: target, behavior: 'smooth' });
        } catch {
          container.scrollTop = target;
        }
      }
    };

    doScroll();

    if (instant) {
      setTimeout(doScroll, 100);
      setTimeout(doScroll, 300);
    }
  }, []);

  // Track if this is initial load
  const isInitialLoadRef = useRef(true);
  const prevMessagesLengthRef = useRef(0);
  const loadedMessageIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (messages.length === 0 || loadingMessages) {
      prevMessagesLengthRef.current = 0;
      return;
    }

    const isInitialLoad = isInitialLoadRef.current;
    const isNewMessage = messages.length > prevMessagesLengthRef.current && prevMessagesLengthRef.current > 0;
    
    prevMessagesLengthRef.current = messages.length;
    
    if (isInitialLoad) {
      isInitialLoadRef.current = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom(true);
        });
      });
    } else if (isNewMessage) {
      requestAnimationFrame(() => {
        scrollToBottom(false);
      });
    }
  }, [messages, loadingMessages, scrollToBottom]);

  // Get available contact data for variable replacement
  const getContactData = useCallback((contactName: string | null): Record<string, string> => {
    return {
      name: contactName || '',
      first_name: contactName?.split(' ')[0] || '',
      last_name: contactName?.split(' ').slice(1).join(' ') || '',
      phone: selectedPhone || '',
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  }, [selectedPhone]);

  // Extract all variables from template content
  const extractVariablesFromContent = useCallback((content: string): string[] => {
    const variablePattern = /\{\{(\w+)\}\}/g;
    const matches = content.matchAll(variablePattern);
    const variables = new Set<string>();
    for (const match of matches) {
      variables.add(match[1].toLowerCase());
    }
    return Array.from(variables);
  }, []);

  // Get variables that need user input (not available from contact data)
  // These will always show input fields regardless of whether user has started typing
  const getVariablesNeedingInput = useCallback((content: string, contactName: string | null): string[] => {
    const allVariables = extractVariablesFromContent(content);
    const contactData = getContactData(contactName);
    
    return allVariables.filter(variable => {
      // Check if contact data has this variable with a non-empty value
      const contactValue = contactData[variable];
      return !(contactValue && contactValue.trim()); // Needs input if no contact data
    });
  }, [extractVariablesFromContent, getContactData]);

  // Check if any variables still need to be filled (for validation before sending)
  const getUnfilledVariables = useCallback((content: string, contactName: string | null): string[] => {
    const variablesNeedingInput = getVariablesNeedingInput(content, contactName);
    
    return variablesNeedingInput.filter(variable => {
      // Check if user has filled this variable
      const userValue = templateVariables[variable];
      return !(userValue && userValue.trim()); // Still unfilled if no user value
    });
  }, [getVariablesNeedingInput, templateVariables]);

  // Process template content by replacing variables with contact data + user input
  const processTemplateContent = useCallback((content: string, contactName: string | null, forSending = false) => {
    let processed = content;
    const contactData = getContactData(contactName);
    
    // First, replace with contact data
    for (const [key, value] of Object.entries(contactData)) {
      if (value) {
        processed = processed.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'gi'), value);
      }
    }
    
    // Then, replace with user-filled variables
    for (const [key, value] of Object.entries(templateVariables)) {
      if (value) {
        processed = processed.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'gi'), value);
      }
    }
    
    // For preview, highlight unfilled variables; for sending, leave them as-is
    if (!forSending) {
      // Replace remaining variables with highlighted placeholders
      processed = processed.replace(/\{\{(\w+)\}\}/g, '[$1]');
    }
    
    return processed;
  }, [getContactData, templateVariables]);

  const handleSendMessage = async () => {
    // Determine the content to send (either from input or from template)
    let textToSend = '';
    
    if (selectedTemplate) {
      const selectedConv = conversations.find(c => c.contactId === selectedContact);
      
      // Check for unfilled variables
      const unfilled = getUnfilledVariables(selectedTemplate.content, selectedConv?.contactName || null);
      if (unfilled.length > 0) {
        setError(`Please fill in missing variables: ${unfilled.join(', ')}`);
        return;
      }
      
      textToSend = processTemplateContent(selectedTemplate.content, selectedConv?.contactName || null, true);
    } else {
      textToSend = messageInput.trim();
    }
    
    if (!textToSend || !selectedPhone || sending) return;

    setMessageInput('');
    setSelectedTemplate(null);
    setTemplateVariables({});
    setSending(true);

    // Add optimistic message
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: SmsMessage = {
      id: tempId,
      direction: 'OUTBOUND',
      content: textToSend,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const result = await sendSms({
        phoneNumber: selectedPhone,
        content: textToSend,
      });

      // Update the optimistic message with real data
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...m,
                id: result.id,
                status: result.status as SmsMessage['status'],
              }
            : m
        )
      );

      // Refresh conversations to update last message
      loadConversations();
    } catch (err) {
      console.error('Error sending SMS:', err);
      // Mark message as failed
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? { ...m, status: 'FAILED' }
            : m
        )
      );
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleRefresh = () => {
    loadConversations();
    if (selectedContact && !selectedContact.startsWith('new-')) {
      // Reload messages for current contact
      getSmsMessages(selectedContact, 1, 100).then((response) => {
        const transformed = response.items.map(transformApiMessage);
        setMessages(transformed);
      });
    }
  };

  const handleSelectContact = (contact: SmsContact) => {
    setSelectedContact(contact.id);
    setSelectedPhone(contact.phoneNumber);
  };

  const selectedConversation = conversations.find((c) => c.contactId === selectedContact);

  const filteredConversations = conversations.filter(
    (c) =>
      (c.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
      c.contactPhone.includes(searchQuery)
  );

  // Show loading state while checking configuration
  if (smsConfigured === null) {
    return (
      <div className="h-[calc(100vh-120px)] flex items-center justify-center rounded-xl border border-gray-800 bg-[#0A0E14] overflow-hidden">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Checking SMS configuration...</p>
        </div>
      </div>
    );
  }

  // Show configuration prompt if SMS not set up
  if (!smsConfigured) {
    return (
      <div className="h-[calc(100vh-120px)] flex items-center justify-center rounded-xl border border-gray-800 bg-[#0A0E14] overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center"
        >
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-600/10 flex items-center justify-center mx-auto mb-6">
            <MessageSquare className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-white mb-3">
            SMS Integration Not Configured
          </h2>
          <p className="text-gray-400 mb-6">
            Connect your SMS provider to start sending and receiving SMS messages.
            We support popular providers like eocean, Twilio, Vonage, and more.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to={ROUTES.CUSTOMER.SETTINGS_SMS}>
              <Button leftIcon={<Settings className="w-4 h-4" />}>
                Configure SMS
              </Button>
            </Link>
            <a href="https://eocean.net" target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" leftIcon={<Globe className="w-4 h-4" />}>
                Learn More
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show error state if there's a persistent error
  if (error && conversations.length === 0) {
    return (
      <div className="h-[calc(100vh-120px)] flex items-center justify-center rounded-xl border border-gray-800 bg-[#0A0E14] overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center"
        >
          <div className="w-20 h-20 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-white mb-3">
            Failed to Load Messages
          </h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={loadConversations} leftIcon={<RefreshCw className="w-4 h-4" />}>
            Retry
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex bg-[#0A0E14] rounded-xl overflow-hidden border border-gray-800">
      {/* Conversations List - Responsive: hidden on mobile, shown on md+ */}
      <div className="hidden md:flex w-80 border-r border-gray-800 flex-col bg-[#121723] shrink-0">
        {/* Header */}
        <div className="p-3 border-b border-gray-800 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-green-500" />
              <h2 className="text-sm font-heading font-semibold text-white">SMS Messages</h2>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => setShowNewSmsModal(true)}
                className="p-1.5 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-green-500 transition-colors"
                title="New SMS"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={handleRefresh}
                className="p-1.5 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors"
                title="Refresh"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="w-3.5 h-3.5" />}
          />
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center">
              <Loader2 className="w-8 h-8 text-green-500 animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Loading conversations...</p>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-6 text-center">
              <MessageSquare className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                {searchQuery ? 'No conversations match your search' : 'No conversations yet'}
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Start a new SMS to begin messaging
              </p>
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.contactId}
                conversation={conversation}
                isActive={selectedContact === conversation.contactId}
                onClick={() => {
                  setSelectedContact(conversation.contactId);
                  setSelectedPhone(conversation.contactPhone);
                }}
              />
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {(selectedConversation || selectedPhone) ? (
          <>
            {/* Chat Header */}
            <div className="h-14 px-4 border-b border-gray-800 flex items-center justify-between bg-[#121723] shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shrink-0">
                  <span className="text-white font-medium text-sm">
                    {(selectedConversation?.contactName || selectedPhone || '?').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-white truncate">
                    {selectedConversation?.contactName || selectedPhone || 'New Contact'}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">{selectedConversation?.contactPhone || selectedPhone}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button className="p-2 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                </button>
                <button className="p-2 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 bg-[#0D1117]" 
              style={{ 
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23374151\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' 
              }}
            >
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 text-green-500 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500">No messages yet</p>
                    <p className="text-sm text-gray-600 mt-1">Send a message to start the conversation</p>
                  </div>
                </div>
              ) : (
                messages.map((message, index) => {
                  const currentDateKey = getDateKey(message.createdAt);
                  const prevMessage = index > 0 ? messages[index - 1] : null;
                  const prevDateKey = prevMessage ? getDateKey(prevMessage.createdAt) : null;
                  const showDateSeparator = currentDateKey !== prevDateKey;
                  
                  // Check if this message is newly added (not in the initial load)
                  const isNew = !loadedMessageIdsRef.current.has(message.id);
                  if (isNew) {
                    loadedMessageIdsRef.current.add(message.id);
                  }
                  
                  return (
                    <div key={message.id}>
                      {showDateSeparator && <DateSeparator date={message.createdAt} />}
                      <MessageBubble message={message} isNew={isNew && !isInitialLoadRef.current} />
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-gray-800 bg-[#121723] shrink-0">
              {/* Template Preview */}
              {selectedTemplate && (() => {
                const varsNeedingInput = getVariablesNeedingInput(selectedTemplate.content, selectedConversation?.contactName || null);
                const unfilledVars = getUnfilledVariables(selectedTemplate.content, selectedConversation?.contactName || null);
                const hasUnfilled = unfilledVars.length > 0;
                const showInputs = varsNeedingInput.length > 0;
                
                return (
                  <div className={`p-3 border-b border-gray-800 ${hasUnfilled ? 'bg-orange-500/5' : 'bg-green-500/5'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className={`w-4 h-4 ${hasUnfilled ? 'text-orange-500' : 'text-green-500'}`} />
                          <span className={`text-xs font-medium ${hasUnfilled ? 'text-orange-400' : 'text-green-400'}`}>
                            {hasUnfilled ? 'Fill missing variables:' : 'Using template:'}
                          </span>
                          <span className="text-xs text-white font-medium truncate">{selectedTemplate.name}</span>
                        </div>
                        
                        {/* Variables Inputs - show all variables that need user input */}
                        {showInputs && (
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            {varsNeedingInput.map((variable) => (
                              <div key={variable} className="relative">
                                <label className="text-[10px] text-gray-500 mb-0.5 block capitalize">{variable.replace(/_/g, ' ')}</label>
                                <Input
                                  placeholder={`Enter ${variable.replace(/_/g, ' ')}`}
                                  value={templateVariables[variable] || ''}
                                  onChange={(e) => setTemplateVariables(prev => ({
                                    ...prev,
                                    [variable]: e.target.value
                                  }))}
                                  className="text-xs"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="p-2.5 bg-[#0D1117] rounded-lg border border-gray-800/50">
                          <p className="text-xs text-gray-400 whitespace-pre-wrap">
                            {processTemplateContent(selectedTemplate.content, selectedConversation?.contactName || null)}
                          </p>
                        </div>
                        
                        {hasUnfilled && (
                          <p className="text-[10px] text-orange-400 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Fill all variables to send this template
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedTemplate(null);
                          setTemplateVariables({});
                        }}
                        className="p-1 text-gray-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })()}
              
              <div className="p-3">
                <div className="flex items-center gap-2">
                  {/* Template Button */}
                  <button
                    onClick={() => setShowTemplateSelector(true)}
                    className={`p-2.5 rounded-lg transition-colors shrink-0 ${
                      selectedTemplate
                        ? 'bg-green-500/20 text-green-400'
                        : 'hover:bg-[#1A2332] text-gray-400 hover:text-white'
                    }`}
                    title="Select template"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  
                  {/* Input or Template Indicator */}
                  {selectedTemplate ? (() => {
                    const hasUnfilled = getUnfilledVariables(selectedTemplate.content, selectedConversation?.contactName || null).length > 0;
                    return (
                      <div className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg border ${
                        hasUnfilled 
                          ? 'bg-orange-500/10 border-orange-500/30' 
                          : 'bg-green-500/10 border-green-500/30'
                      }`}>
                        <span className={`text-sm ${hasUnfilled ? 'text-orange-400' : 'text-green-400'}`}>
                          {hasUnfilled ? 'Fill missing variables above' : 'Template ready to send'}
                        </span>
                        {hasUnfilled ? (
                          <AlertCircle className="w-4 h-4 text-orange-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    );
                  })() : (
                    <Input
                      ref={inputRef}
                      placeholder="Type your SMS message..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="flex-1 min-w-0"
                      maxLength={1600}
                    />
                  )}
                  
                  <Button
                    onClick={handleSendMessage}
                    disabled={
                      sending || 
                      !selectedPhone || 
                      (selectedTemplate 
                        ? getUnfilledVariables(selectedTemplate.content, selectedConversation?.contactName || null).length > 0
                        : !messageInput.trim()
                      )
                    }
                    isLoading={sending}
                    className="shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Character Count (only for manual input) */}
                {!selectedTemplate && (
                  <div className="flex items-center justify-between mt-2 px-1">
                    <span className="text-xs text-gray-500">
                      {messageInput.length}/160 characters
                    </span>
                    {messageInput.length > 160 && (
                      <span className="text-xs text-orange-400">
                        Message will be split into {Math.ceil(messageInput.length / 160)} parts
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* No Chat Selected */
          <div 
            className="flex-1 flex flex-col items-center justify-center bg-[#0D1117] text-center p-8"
            style={{ 
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23374151\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' 
            }}
          >
            <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mb-6">
              <MessageSquare className="w-12 h-12 text-green-500" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-white mb-3">
              Select a conversation
            </h3>
            <p className="text-gray-400 mb-6 max-w-sm">
              Choose a conversation from the list or start a new one to begin messaging.
            </p>
            <Button
              onClick={() => setShowNewSmsModal(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              New SMS
            </Button>
          </div>
        )}
      </div>

      {/* New SMS Modal */}
      <AnimatePresence>
        <NewSmsModal
          isOpen={showNewSmsModal}
          onClose={() => setShowNewSmsModal(false)}
          onSelect={(contact) => {
            handleSelectContact(contact);
            setShowNewSmsModal(false);
          }}
          existingConversationIds={conversations.map((c) => c.contactId)}
        />
      </AnimatePresence>

      {/* SMS Template Selector Modal */}
      <AnimatePresence>
        <SmsTemplateSelector
          isOpen={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          onSelect={(template) => {
            setSelectedTemplate(template);
            setTemplateVariables({}); // Reset variables for new template
            setMessageInput(''); // Clear manual input when template is selected
          }}
          contactName={selectedConversation?.contactName}
        />
      </AnimatePresence>
    </div>
  );
};

export default SmsMessagesPage;
